import React from 'react';
import {
  WheelSettings,
  StanceSettings,
  WheelArchetype,
  WheelDiameter,
  WheelFinish,
  TireTreatment,
} from '../../types/vehicle';
import { Disc, CircleDot, Shield, Check } from 'lucide-react';

export interface WheelBayProps {
  wheel: WheelSettings;
  stance: StanceSettings;
  onChangeWheel: (updates: Partial<WheelSettings>) => void;
  onChangeStance: (updates: Partial<StanceSettings>) => void;
}

export const WheelBay: React.FC<WheelBayProps> = ({
  wheel,
  stance,
  onChangeWheel,
  onChangeStance,
}) => {
  const archetypes: {
    id: WheelArchetype;
    title: string;
    era: string;
    icon: React.ReactNode;
    desc: string;
  }[] = [
    {
      id: 'slotted_mags',
      title: 'Slotted Mags',
      era: 'Ansen Sprint 1965+',
      icon: <CircleDot className="w-5 h-5 text-safety-amber" />,
      desc: 'Deep dish kidney bean slots with polished center dome & acorn lugs.',
    },
    {
      id: 'steelies',
      title: 'Steelies & Poverty Caps',
      era: 'Police / Sleeper 1968+',
      icon: <Disc className="w-5 h-5 text-shop-muted" />,
      desc: 'Solid steel rim with chrome "Dog Dish" poverty center cap.',
    },
    {
      id: 'torq_thrusts',
      title: 'Torq Thrust D-Spoke',
      era: 'American Racing Classic',
      icon: <CircleDot className="w-5 h-5 text-safety-cyan" />,
      desc: '5 contoured D-spokes radiating outward with mirror-polished outer lip.',
    },
    {
      id: 'mesh',
      title: 'Cross-Lace Mesh',
      era: 'Pro-Touring / Trans-Am',
      icon: <Shield className="w-5 h-5 text-safety-amber" />,
      desc: '16 interlocking diamond mesh spokes with perimeter assembly rivets.',
    },
  ];

  const diameters: WheelDiameter[] = [15, 17, 18, 20];

  const finishes: { id: WheelFinish; label: string; bg: string }[] = [
    { id: 'polished', label: 'Polished', bg: '#cbd5e1' },
    { id: 'machined_gray', label: 'Machined Gray', bg: '#64748b' },
    { id: 'matte_black', label: 'Matte Black', bg: '#18181b' },
    { id: 'chrome', label: 'Chrome', bg: '#e2e8f0' },
    { id: 'gold', label: 'Gold Anodized', bg: '#d97706' },
  ];

  const tireTreatments: { id: TireTreatment; label: string; desc: string }[] = [
    { id: 'blackwall', label: 'Blackwall', desc: 'Standard satin performance radial' },
    { id: 'white_letter', label: 'White Letter (Radial T/A)', desc: 'Raised white lettering along upper curve' },
    { id: 'redline', label: 'Redline', desc: 'Vintage 1960s muscle car red sidewall ring' },
    { id: 'goldline', label: 'Goldline', desc: 'Corvette / Hurst special edition gold ring' },
  ];

  return (
    <div className="space-y-6">
      {/* 1. Wheel Archetype Selector */}
      <div className="space-y-2">
        <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-shop-muted">
          1. Wheel Archetype &amp; Architecture
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          {archetypes.map((item) => {
            const isSelected = wheel.archetype === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onChangeWheel({ archetype: item.id })}
                className={`min-h-[72px] p-3 rounded border text-left flex items-start gap-2.5 transition-all duration-100 cursor-pointer ${
                  isSelected
                    ? 'bg-shop-panel text-shop-highlight border-safety-amber shadow-[inset_0_2px_0_0_#f59e0b]'
                    : 'bg-shop-dark text-shop-muted border-shop-border hover:bg-shop-well hover:text-shop-light'
                }`}
              >
                <div className="mt-0.5 shrink-0">{item.icon}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-shop-highlight">
                      {item.title}
                    </span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-safety-amber shrink-0" />}
                  </div>
                  <div className="text-[10px] font-mono text-safety-amber font-semibold mt-0.5">
                    {item.era}
                  </div>
                  <p className="text-[10px] text-shop-muted line-clamp-2 mt-0.5">
                    {item.desc}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Wheel Diameter & Staggered Setup */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-shop-muted">
            2. Rim Diameter (Constant 27.0&quot; Rolling Tire Outer Diameter)
          </h4>
          <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-mono">
            <input
              type="checkbox"
              checked={stance.staggeredWheels}
              onChange={(e) => onChangeStance({ staggeredWheels: e.target.checked })}
              className="w-4 h-4 accent-safety-amber rounded cursor-pointer"
            />
            <span className="text-shop-light font-bold">Staggered Setup (F/R)</span>
          </label>
        </div>

        {!stance.staggeredWheels ? (
          /* Uniform Diameter */
          <div className="grid grid-cols-4 gap-2">
            {diameters.map((dia) => {
              const isSelected = stance.frontWheelDiameter === dia;
              return (
                <button
                  key={dia}
                  onClick={() =>
                    onChangeStance({
                      frontWheelDiameter: dia,
                      rearWheelDiameter: dia,
                    })
                  }
                  className={`min-h-[48px] py-2 px-3 rounded border text-center font-mono font-bold transition-all duration-100 cursor-pointer ${
                    isSelected
                      ? 'bg-safety-amber text-shop-darkest border-safety-amberDark shadow-[0_2px_0_0_#92400e]'
                      : 'bg-shop-dark text-shop-light border-shop-border hover:bg-shop-panel'
                  }`}
                >
                  <span className="text-sm block">{dia}&quot;</span>
                  <span className="text-[10px] block opacity-80">
                    {dia === 15 ? '6.0" Sidewall' : dia === 17 ? '5.0" Sidewall' : dia === 18 ? '4.5" Sidewall' : '3.5" Sidewall'}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          /* Staggered Diameters */
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-shop-darkest border border-shop-border rounded">
            <div className="space-y-1.5">
              <span className="text-[11px] font-mono uppercase tracking-wider text-shop-muted block">
                Front Rim Diameter:
              </span>
              <div className="grid grid-cols-4 gap-1.5">
                {diameters.map((dia) => (
                  <button
                    key={`f-${dia}`}
                    onClick={() => onChangeStance({ frontWheelDiameter: dia })}
                    className={`min-h-[44px] rounded border font-mono font-bold text-xs cursor-pointer ${
                      stance.frontWheelDiameter === dia
                        ? 'bg-safety-amber text-shop-darkest border-safety-amberDark'
                        : 'bg-shop-panel text-shop-light border-shop-border hover:bg-shop-well'
                    }`}
                  >
                    {dia}&quot;
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <span className="text-[11px] font-mono uppercase tracking-wider text-shop-muted block">
                Rear Rim Diameter:
              </span>
              <div className="grid grid-cols-4 gap-1.5">
                {diameters.map((dia) => (
                  <button
                    key={`r-${dia}`}
                    onClick={() => onChangeStance({ rearWheelDiameter: dia })}
                    className={`min-h-[44px] rounded border font-mono font-bold text-xs cursor-pointer ${
                      stance.rearWheelDiameter === dia
                        ? 'bg-safety-amber text-shop-darkest border-safety-amberDark'
                        : 'bg-shop-panel text-shop-light border-shop-border hover:bg-shop-well'
                    }`}
                  >
                    {dia}&quot;
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3. Wheel Finish & Tire Treatment */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Finish Selector */}
        <div className="space-y-2">
          <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-shop-muted">
            3. Wheel Spoke &amp; Rim Finish
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {finishes.map((f) => {
              const isSelected = wheel.finish === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => onChangeWheel({ finish: f.id })}
                  className={`min-h-[48px] p-2 rounded border text-left flex items-center gap-2 cursor-pointer transition-all duration-100 ${
                    isSelected
                      ? 'bg-shop-panel border-safety-amber shadow-[inset_0_2px_0_0_#f59e0b]'
                      : 'bg-shop-dark border-shop-border hover:bg-shop-well'
                  }`}
                >
                  <span
                    className="w-4 h-4 rounded-full border border-shop-border shrink-0"
                    style={{ backgroundColor: f.bg }}
                  ></span>
                  <span className="text-xs font-bold text-shop-highlight truncate">
                    {f.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tire Treatment Selector */}
        <div className="space-y-2">
          <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-shop-muted">
            4. Tire Sidewall Treatment
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {tireTreatments.map((t) => {
              const isSelected = wheel.tireTreatment === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => onChangeWheel({ tireTreatment: t.id })}
                  className={`min-h-[48px] p-2.5 rounded border text-left flex flex-col justify-center cursor-pointer transition-all duration-100 ${
                    isSelected
                      ? 'bg-shop-panel border-safety-amber shadow-[inset_0_2px_0_0_#f59e0b]'
                      : 'bg-shop-dark border-shop-border hover:bg-shop-well'
                  }`}
                >
                  <span className="text-xs font-bold text-shop-highlight truncate">
                    {t.label}
                  </span>
                  <span className="text-[10px] text-shop-muted truncate mt-0.5">
                    {t.desc}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 4. Archetype Specific Hardware Toggles */}
      {(wheel.archetype === 'steelies' || wheel.archetype === 'mesh') && (
        <div className="p-3 bg-shop-darkest border border-shop-border rounded flex items-center gap-6">
          {wheel.archetype === 'steelies' && (
            <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-mono">
              <input
                type="checkbox"
                checked={wheel.hasBeautyRings ?? true}
                onChange={(e) => onChangeWheel({ hasBeautyRings: e.target.checked })}
                className="w-4 h-4 accent-safety-amber rounded cursor-pointer"
              />
              <span className="text-shop-light font-bold">
                Polished Stainless Outer Beauty Rings
              </span>
            </label>
          )}

          {wheel.archetype === 'mesh' && (
            <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-mono">
              <input
                type="checkbox"
                checked={wheel.hasRivetBolts ?? true}
                onChange={(e) => onChangeWheel({ hasRivetBolts: e.target.checked })}
                className="w-4 h-4 accent-safety-amber rounded cursor-pointer"
              />
              <span className="text-shop-light font-bold">
                Perimeter Multi-Piece Assembly Rivets
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
};
